export type TimeSeriesTableHelpTabId = 'multiEdit' | 'fillHandle' | 'copyFrom' | 'calculate'

export type TimeSeriesTableHelpTab = {
  id: TimeSeriesTableHelpTabId
  label: string
  intro: string
  steps: string[]
  tips: string[]
  demoHint: string
}

/**
 * What the active system actually lets the user do in the time series table. Derived by
 * `TimeSeriesSettingTable`, which already holds the feature flag, the dataset list and the
 * write-access flag — the dialog never queries them itself.
 */
export type TimeSeriesTableFeatureAvailability = {
  editingEnabled: boolean
  canCopyFromOtherDataset: boolean
  canCalculate: boolean
}

export type Translate = (key: string) => string

const TAB_ORDER: TimeSeriesTableHelpTabId[] = ['multiEdit', 'fillHandle', 'copyFrom', 'calculate']

export function getAvailableTimeSeriesTableHelpTabIds({
  editingEnabled,
  canCopyFromOtherDataset,
  canCalculate,
}: TimeSeriesTableFeatureAvailability): TimeSeriesTableHelpTabId[] {
  if (!editingEnabled) {
    return []
  }

  const isAvailable: Record<TimeSeriesTableHelpTabId, boolean> = {
    multiEdit: true,
    fillHandle: true,
    copyFrom: canCopyFromOtherDataset,
    calculate: canCalculate,
  }

  return TAB_ORDER.filter((id) => isAvailable[id])
}

/**
 * `hasSelectionCheckboxes` says whether the table has the Computed setting checkbox column — only
 * systems with the calculate feature do, so the text never mentions checkboxes the user cannot see.
 */
export function getTimeSeriesTableHelpTabs(
  t: Translate,
  availableIds: TimeSeriesTableHelpTabId[],
  hasSelectionCheckboxes: boolean
): TimeSeriesTableHelpTab[] {
  const tabs = buildTimeSeriesTableHelpTabs(t, hasSelectionCheckboxes)
  return availableIds.map((id) => tabs[id])
}

function onlyWith(condition: boolean, text: string): string[] {
  return condition ? [text] : []
}

function buildTimeSeriesTableHelpTabs(
  t: Translate,
  hasSelectionCheckboxes: boolean
): Record<TimeSeriesTableHelpTabId, TimeSeriesTableHelpTab> {
  return {
    multiEdit: {
      id: 'multiEdit',
      label: t('Edit several cells'),
      intro: t(
        'Select the hours you want to change and type the value once — it is written to every selected cell at the same time.'
      ),
      steps: [
        t(
          'Click a cell to select it — clicking another cell moves the selection there. Hold and drag across the column to select several hours.'
        ),
        t('Hold Shift and click to select the whole range from your last selection.'),
        t('Hold Cmd (Mac) or Ctrl (Windows) and click to add single hours to the selection.'),
        ...onlyWith(
          hasSelectionCheckboxes,
          t('You can also tick the boxes in the Computed setting column to pick separate hours.')
        ),
        t('Type the value in any of the selected cells. Every selected hour gets the same value.'),
      ],
      tips: [
        t(
          'Clicking a cell inside the selection keeps it, so you can type straight into the whole selection. Click outside the table to clear it.'
        ),
        ...onlyWith(
          hasSelectionCheckboxes,
          t(
            'The Select all checkbox applies to the rows on the page you are on, not the whole period. The selection is cleared when you change page.'
          )
        ),
        t(
          'You can paste a column from Excel. Several values are inserted row by row from the cell you paste into; a single value pasted into a selection fills the whole selection.'
        ),
      ],
      demoHint: t('Click a cell, click another to move the selection, then drag and type a value.'),
    },
    fillHandle: {
      id: 'fillHandle',
      label: t('Fill by dragging'),
      intro: t(
        'The small square in the bottom right corner of a cell copies the value of that cell to every cell you drag across.'
      ),
      steps: [
        t('Point at the cell holding the value you want to copy. The square appears in the bottom right corner.'),
        t('Hold the square down and drag up or down across the hours that should get the same value.'),
        t('Release. The whole range gets the value of the source cell and stays selected.'),
      ],
      tips: [
        t('Dragging from the cell itself instead of from the square only selects the range — no values are changed.'),
        t(
          'The table scrolls automatically when you drag towards the top or bottom edge, so you can fill further than one screen height.'
        ),
        t('The range stays selected after you release, so you can type a new value straight into the whole range.'),
      ],
      demoHint: t('Point at a cell, grab the square in the corner and drag downwards.'),
    },
    copyFrom: {
      id: 'copyFrom',
      label: t('Copy from another series'),
      intro: t(
        'Copy from fetches values from another series in the table — a forecast or an optimization result, for example — into the column you are editing.'
      ),
      steps: [
        t('Select the hours you want to fill. Skip the selection if the whole period should be filled.'),
        t('Click Copy from in the column header.'),
        t('Choose the series you want to fetch the values from. The values are written straight away.'),
      ],
      tips: [
        t('Without a selection the whole column is overwritten.'),
        t('Hours where the chosen series has no value are left untouched — existing values remain.'),
        t('The menu lists every series in the table except the one you are editing.'),
      ],
      demoHint: t('Select a few rows and click Copy from in the header.'),
    },
    calculate: {
      id: 'calculate',
      label: t('Calculate new values'),
      intro: t('Calculate values from another series — either with an offset in MW or with a percentage adjustment.'),
      steps: [
        t('Tick the boxes in the Calculated column for the hours that should be calculated.'),
        t('Choose the series the calculation should start from.'),
        t('Enter either MW or percent and click Calculate.'),
      ],
      tips: [
        t('Percent is given as an adjustment: 5 gives +5 % (the value times 1.05) and −5 gives −5 %.'),
        t('MW is added to the source value: 10 gives the source value plus 10.'),
        t('Only one of the fields can be used at a time — filling in both gives an error message.'),
        t(
          'Hours where the source series has no value are left empty, and the selection is cleared once the calculation is done.'
        ),
      ],
      demoHint: t('Select rows, enter MW or percent and click Calculate.'),
    },
  }
}
